import {
  CustomFieldProps,
  DynamicField,
  DynamicForm,
  FieldVariant,
  SelectFieldProps,
  SelectOption,
  TextFieldProps,
} from '@/components/form-builder/form-builder.types';
import { DacRepository } from '@/store/dac-state/dac-state.repository';
import { DocType, Scenario } from '@/types';
import { BehaviorSubject, combineLatest, map, Observable, of, switchMap } from 'rxjs';
import { MultiFormAccordionService } from '@/components/multi-form-accordion/multi-form-accordion.service';
import { DacSubmissionPiecesTabsComponent } from '../dac-submission-pieces-tabs/dac-submission-pieces-tabs.component';
import { ObservableFormBuilderResolverComponent } from '@/components/form-builder/utils/observable-form-builder-resolver';
import { DacScenarioInfosComponent } from '../../dac-scenario-infos/dac-scenario-infos.component';

interface getDacConfigurationUpdateFormObjectProps {
  store: DacRepository;
  dacId?: number;
  dacVer?: number;
  isUpdatable?: Observable<boolean>;
  multiFormAccordionsForSubmissionPieces: Observable<MultiFormAccordionService[]>;
  docTypes: Observable<DocType[]>;
  scenarios: Observable<Scenario[]>;
  translate: (key: string, params?: Record<string, any>) => string;
}

export const getDacConfigurationUpdateFormObject = ({
  store,
  dacId,
  dacVer,
  isUpdatable,
  multiFormAccordionsForSubmissionPieces,
  docTypes,
  scenarios,
  translate,
}: getDacConfigurationUpdateFormObjectProps): DynamicForm => {
  const isDisabled = isUpdatable?.pipe(map(updatable => !updatable)) as Observable<boolean>;
  const etapeDac$ = store.getNestedObservable<string>('dacInfo', 'etapeDac');
  const selectedEtapeToConfigure$ = new BehaviorSubject<string>('');
  
  const effectiveSelectedEtape$ = combineLatest([etapeDac$, selectedEtapeToConfigure$]).pipe(
    map(([etapeDac, selected]) => selected || etapeDac)
  );

  const selectedScenario$ = combineLatest([
    store.getObservable<SelectOption>('scenarioIdSelected'),
    scenarios
  ]).pipe(
    map(([selected, scenariosList]) => {
      if (!selected?.code) return null;
      return scenariosList.find(s => s.id === selected.code) ?? null;
    })
  );

  const refField: DynamicField<TextFieldProps> = {
    label: 'dac.form.reference',
    variant: FieldVariant.TEXT,
    isRequired: true,
    isDisabled: of(true),
    props: {
      value: store.getNestedObservable<string>('dacInfo', 'reference'),
    },
  };

  const marketTypeField: DynamicField<TextFieldProps> = {
    label: 'dac.form.marketType',
    variant: FieldVariant.TEXT,
    isRequired: true,
    isDisabled: of(true),
    props: {
      value: store.getNestedObservable<string>('dacInfo', 'typeMarche', 'description'),
    },
  };

  const passingModeField: DynamicField<TextFieldProps> = {
    label: 'dac.form.passingMode',
    variant: FieldVariant.TEXT,
    isRequired: true,
    isDisabled: of(true),
    props: {
      value: store.getNestedObservable<string>('dacInfo', 'modePassation', 'libelle'),
    },
  };

  const scenarioIdField: DynamicField<SelectFieldProps> = {
    label: 'Scénario',
    variant: FieldVariant.SELECT,
    isRequired: true,
    isDisabled,
    error: store.getNestedObservable<string>('error', 'scenarioId'),
    props: {
      value: store.getObservable<SelectOption>('scenarioIdSelected'),
      onSelectChange: (value: SelectOption) => {
        store.set('scenarioIdSelected', value);
        selectedEtapeToConfigure$.next(''); // Reset selection on scenario change
        store.setNested('dacUpdateConfigDto', 'scenarioId', value?.code);
        store.setNested('error', 'scenarioId', '');
      },
      placeholder: 'Identifiant du scénario',
      options: scenarios.pipe(map(scenarios => scenarios.map(scenario => ({ code: scenario.id, name: scenario.nom })))),
    },
  };

  const scenarioInfosField: DynamicField<CustomFieldProps> = {
    label: '',
    variant: FieldVariant.CUSTOM,
    isHidden: store.getObservable<SelectOption>('scenarioIdSelected').pipe(map(scenarioId => !scenarioId?.code)),
    props: {
      component: DacScenarioInfosComponent,
      injectKeys: ['scenarioData', 'selectedEtape', 'onEtapeSelect'],
      scenarioData: selectedScenario$,
      selectedEtape: effectiveSelectedEtape$,
      onEtapeSelect: (etapeId: string) => {
        selectedEtapeToConfigure$.next(etapeId);
      }
    },
  };

  const submissionFolderObservableSubFormBuilder = combineLatest([docTypes, multiFormAccordionsForSubmissionPieces, selectedScenario$, effectiveSelectedEtape$]).pipe(
    map(([docTypesValue, multiFormAccordionForSubmissionPiecesValue, selectedScenario, etapeDac]) => {
      const scenarioTypeDossierIds = new Set(
        (selectedScenario?.scenarioEtapes ?? [])
          .filter(step => {
            if (!etapeDac) return true;
            if (String(step.etapeCode) === String(etapeDac)) return true;
            if (String(step.etapeId) === String(etapeDac)) return true;
            if (String(etapeDac) === '1' && (step.etapeCode === 'ETAPE_UNIQUE' || step.etapeId === 4)) return true;
            return false;
          })
          .map(e => e.typeDossierId)
      );

      const filtered = docTypesValue
        .map((docType, index) => ({ docType, accordion: multiFormAccordionForSubmissionPiecesValue[index] }))
        .filter(({ docType }) => scenarioTypeDossierIds.has(docType.id));

      return {
        title: '',
        description: '',
        isHeaderHidden: true,
        grids: [
          {
            title: '',
            isHeaderHidden: true,
            gridItems: [
              {
                fields: [
                  {
                    label: '',
                    variant: FieldVariant.CUSTOM,
                    props: {
                      component: DacSubmissionPiecesTabsComponent,
                      injectKeys: ['docTypes', 'accordions', 'dacId', 'dacVer', 'isDisabled', 'translate'],
                      docTypes: filtered.map(f => f.docType),
                      accordions: filtered.map(f => f.accordion),
                      dacId,
                      dacVer,
                      isDisabled,
                      translate,
                    },
                  },
                ],
              },
            ],
          },
        ],
      } satisfies DynamicForm;
    })
  );

  return {
    title: '',
    description: '',
    isHeaderHidden: true,
    grids: [
      {
        title: '',
        isHeaderHidden: true,
        gridItems: [
          {
            fields: [refField],
          },
          {
            fields: [marketTypeField, passingModeField],
          },
          {
            fields: [scenarioIdField],
          },
          {
            fields: [scenarioInfosField],
          },
        ],
      },
      {
        title: 'dac.form.tabs.submission',
        isHeaderHidden: store.getObservable<SelectOption>('scenarioIdSelected').pipe(map(scenarioId => !scenarioId?.code)),
        gridItems: [
          {
            fields: [
              {
                label: '',
                variant: FieldVariant.CUSTOM,
                props: {
                  component: ObservableFormBuilderResolverComponent,
                  injectKeys: ['formObject'],
                  formObject: submissionFolderObservableSubFormBuilder,
                },
              },
            ],
          },
        ],
      },
    ],
  };
};
